import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";
import ChartFrame from "./ChartFrame";
import { TrendingUp } from "lucide-react";

function shortDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short" });
  } catch { return iso; }
}

/**
 * 7-day bar chart of daily calories + protein for the current user.
 * A gold reference line marks each day's goal (if set).
 */
export default function NutritionTrendChart() {
  const { user } = useAuth();
  const [days, setDays] = useState([]);
  const [metric, setMetric] = useState("calories");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/nutrition", { params: { days: 7 } })
      .then((r) => setDays(r.data.days || []))
      .catch(() => setDays([]))
      .finally(() => setLoaded(true));
  }, []);

  const chart = useMemo(() => {
    // Backfill any missing dates in the last 7 days with 0s so the chart always shows 7 bars.
    const map = new Map(days.map((d) => [d.day, d]));
    const out = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const row = map.get(iso) || { day: iso, calories: 0, protein_g: 0 };
      out.push({
        day: iso,
        short: shortDate(iso),
        calories: Math.round(row.calories || 0),
        protein_g: Math.round(row.protein_g || 0),
      });
    }
    return out;
  }, [days]);

  const goal = metric === "calories" ? user?.calorie_goal : user?.protein_goal_g;
  const yKey = metric === "calories" ? "calories" : "protein_g";
  const unit = metric === "calories" ? "kcal" : "g";

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="nutrition-trend-chart">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" /> // Trend · 7 days
          </div>
          <div className="text-slate-900 dark:text-white font-display font-black uppercase text-xl leading-none mt-1">
            {metric === "calories" ? "Daily Calories" : "Daily Protein"}
          </div>
        </div>
        <div className="flex border border-slate-200">
          {["calories", "protein_g"].map((k) => {
            const active = metric === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setMetric(k)}
                data-testid={`trend-metric-${k}`}
                className={[
                  "px-3 py-1.5 font-mono-num text-[10px] uppercase tracking-widest transition-colors border-r border-slate-200 last:border-r-0",
                  active ? "bg-[#0D9488] text-black font-semibold" : "bg-slate-50 text-slate-600 hover:bg-slate-50",
                ].join(" ")}
              >
                {k === "calories" ? "kcal" : "protein"}
              </button>
            );
          })}
        </div>
      </div>

      <ChartFrame className="h-56">
        {chart.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="short" tick={{ fill: "#64748B", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
            <YAxis tick={{ fill: "#64748B", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
            <Tooltip
              cursor={{ fill: "#0D948810" }}
              contentStyle={{ background: "#FFFFFF", border: "1px solid #0D9488", borderRadius: 0, fontSize: 12, color: "#0F172A" }}
              labelStyle={{ color: "#0D9488", fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10 }}
              itemStyle={{ color: "#0F172A" }}
              formatter={(v) => [`${v} ${unit}`, metric === "calories" ? "Calories" : "Protein"]}
              labelFormatter={(_, arr) => (arr?.[0]?.payload?.day) || ""}
            />
            {goal != null && goal > 0 && (
              <ReferenceLine
                y={goal}
                stroke="#0D9488"
                strokeDasharray="4 4"
                label={{ value: `goal ${goal}`, fill: "#0D9488", fontSize: 10, fontFamily: "monospace", position: "insideTopRight" }}
              />
            )}
            <Bar dataKey={yKey} radius={[2, 2, 0, 0]}>
              {chart.map((d, i) => {
                const hit = goal != null && d[yKey] >= Number(goal);
                return <Cell key={i} fill={hit ? "#0D9488" : "#CBD5E1"} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-slate-400 text-xs font-mono-num uppercase tracking-widest">
            {loaded ? "No data yet" : "Loading…"}
          </div>
        )}
      </ChartFrame>
      {!loaded && chart.length > 0 && (
        <div className="text-slate-400 text-[11px] font-mono-num uppercase tracking-widest text-center mt-2">Loading…</div>
      )}
    </Card>
  );
}
